import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { THEME } from '../../../../../../../components-v2/Theme/theme';
import { Button } from '../../../../../../../components/Button';
import { InlineValueEditor } from '../../../../../../../components/entities/InlineValueEditor';
import { KeyFactWithKeyDate } from '../../../../../../../components/entities/KeyFactWithKeyDate';
import { EditItemPenIcon } from '../../../../../../../components/svgs/icons/EditItemPenIcon';
import { PrivateVisibilityIcon } from '../../../../../../../components/svgs/icons/PrivateVisibilityIcon';
import { TrashIcon } from '../../../../../../../components/svgs/icons/TrashIcon';
import { XIcon } from '../../../../../../../components/svgs/icons/XIcon';
import { PopoverType, TinyPopover } from '../../../../../../../components/TinyPopover';
import { SimpleTooltip, TooltipProvider } from '../../../../../../../components/Tooltip';
import { grayIconFill } from '../../../../../../../styles/colors';
import { bs } from '../../../../../../../styles/styles';
import { styleSheet } from '../../styles';

export const KeyFactItem = ({
	disableEdit,
	keyFact,
	contact,
	isEditing,
	isDeleting,
	onEditClicked,
	onEditCanceled,
	onEditTextChanged,
	onSaveEdit,
	onDeleteCanceled,
	onDeleteClicked,
	onDeleteConfirmed,
	editableKeyFact,
	canEdit,
}: {
	disableEdit: boolean;
	keyFact: Api.IKeyFact;
	contact?: Api.IProjectedContact | Api.IContact;
	isEditing: boolean;
	isDeleting: boolean;
	onEditClicked: (item: Api.IKeyFact) => void;
	onEditCanceled: () => void;
	onEditTextChanged: (value: string) => void;
	onSaveEdit: () => void;
	onDeleteCanceled: () => void;
	onDeleteClicked: () => void;
	onDeleteConfirmed: () => void;
	editableKeyFact: Api.IKeyFact | null;
	canEdit: boolean;
}) => {
	// Only allow editing if canEdit is true - this prevents accidental deletion via clearing the value.
	// Date-based key facts (birthday, anniversary, etc.) are edited via the EditKeyFactModal, opened from
	// KeyFacts; only free-text key facts fall through to this inline editor.
	if (isEditing && canEdit) {
		return (
			<InlineValueEditor
				autoComplete='off'
				autoFocus={true}
				compactLayout={true}
				disabled={disableEdit}
				onCancelButtonClicked={onEditCanceled}
				onChange={ev => onEditTextChanged(ev.target.value)}
				onKeyDown={ev => {
					if (ev.keyCode === 13) {
						onSaveEdit();
					}
				}}
				onSaveButtonClicked={onSaveEdit}
				value={editableKeyFact?.value || ''}
			/>
		);
	}

	// Determine if edit/delete buttons should be shown based on canEdit prop
	// Logic is now centralized in the canEditKeyFact utility function
	let editActions: React.ReactNode = null;

	if (!canEdit) {
		// Key fact cannot be edited - show lock icon with tooltip
		const sourceName = keyFact?.source?.source;
		const tooltipMessage = sourceName
			? `This information is from ${sourceName} and cannot be edited or deleted.`
			: 'This information cannot be edited or deleted.';

		editActions = (
			<SimpleTooltip title={tooltipMessage}>
				<div className={css(styleSheet.keyFactItemActionButton)} style={{ display: 'flex', alignItems: 'center' }}>
					<PrivateVisibilityIcon fillColor={grayIconFill} />
				</div>
			</SimpleTooltip>
		);
	} else {
		// Key fact can be edited - show edit and delete buttons
		editActions = (
			<>
				<Button
					kind='icon'
					size='small'
					className={css(styleSheet.keyFactItemActionButton)}
					onClick={() => onEditClicked(keyFact)}
				>
					<EditItemPenIcon />
				</Button>

				<TinyPopover
					align='end'
					anchor={
						<Button
							disabled={disableEdit}
							kind='icon'
							onClick={onDeleteClicked}
							className={css(styleSheet.keyFactItemActionButton)}
						>
							<TrashIcon />
						</Button>
					}
					arrow={{
						arrowSize: 10,
					}}
					arrowStyles={[styleSheet.deleteItemConfirmationArrow]}
					dismissOnOutsideAction={true}
					isOpen={isDeleting}
					onRequestClose={onDeleteCanceled}
					placement={['top']}
					type={PopoverType.blue}
				>
					<div className={css(styleSheet.deleteItemConfirmationContainer)}>
						<div className={css(styleSheet.closeDeleteConfirmationContainer)} onClick={onDeleteCanceled}>
							<XIcon color={THEME.colors.text.light} height={12} width={12} />
						</div>
						<p>Do you want to delete this key fact?</p>
						<Button size='small' className={css(bs.ctaButtonDestructiveSolid)} onClick={onDeleteConfirmed}>
							Delete
						</Button>
					</div>
				</TinyPopover>
			</>
		);
	}

	return (
		<TooltipProvider>
			<li className={css(styleSheet.keyFactItem)}>
				<div className={css(styleSheet.keyFactItemValueContainer)}>
					<div className={css(styleSheet.keyFactItemValue)}>
						{keyFact.keyDate ? (
							<KeyFactWithKeyDate keyFact={keyFact} contact={contact} readonly={disableEdit} />
						) : (
							keyFact.value
						)}
					</div>
				</div>
				{editActions && (
					<div className={`${css(styleSheet.keyFactItemActions)} keyFactItemActions ${isDeleting ? 'deleting' : ''}`}>
						{editActions}
					</div>
				)}
			</li>
		</TooltipProvider>
	);
};
